import React, { useEffect, useRef, useState } from 'react';
import { X, Database, CloudArrowDown, Warning, Trash, CalendarBlank } from '@phosphor-icons/react';
import { DateRange } from 'react-date-range';
import { es } from 'date-fns/locale';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { fileExplorerService } from '../../../services/files/fileExplorerService.js';
import { HttpError } from '../../../services/http/httpClient.js';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog.jsx';

const PREVIEW_DEBOUNCE_MS = 400;
const DAY_MS = 24 * 60 * 60 * 1000;

// Precisión de día (no solo mes/año) para el resumen y el diálogo de
// confirmación: con los presets cortos ("Últimas 24 horas", "Últimos 7 días")
// dos fechas del mismo mes se verían idénticas si solo mostraran mes/año.
const formatDayMonthYear = (isoDate) => {
  if (!isoDate) return '—';
  return new Date(isoDate).toLocaleDateString('es-AR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateTime = (isoDate) => {
  if (!isoDate) return '—';
  const date = new Date(isoDate);
  const datePart = date.toLocaleDateString('es-AR');
  const timePart = date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  return `${datePart} · ${timePart}`;
};

const formatSize = (bytes) => {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

const formatDisplayDate = (date) => {
  if (!date) return '';
  return date.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const mapMaintenanceErrorToMessage = (err, accion) => {
  if (err instanceof HttpError) {
    if (err.status === 403) return 'No tienes permiso para realizar esta acción.';
    if (err.status === 400) return `No se pudo ${accion}: la solicitud es inválida.`;
    if (err.status === 404) return 'La función de mantenimiento todavía no está disponible en el servidor.';
  }
  return `Ocurrió un error inesperado al intentar ${accion}. Inténtalo de nuevo.`;
};

/**
 * Backup completo del árbol + borrado masivo por rango de fechas, separados
 * en tabs (brief.md de mockups/explorador-archivos-mantenimiento/, revisado
 * 2026-07-31). Contrato de backend asumido — ver comentarios en
 * fileExplorerService.js — todavía no implementado del lado del servidor.
 */
const FileExplorerMaintenanceModal = ({ isOpen, onClose, onDeleted, onNotify }) => {
  const [activeTab, setActiveTab] = useState('backup');

  const [loadingInfo, setLoadingInfo] = useState(false);
  const [lastBackup, setLastBackup] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [generatingBackup, setGeneratingBackup] = useState(false);

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [showRangePicker, setShowRangePicker] = useState(false);
  const [rangePickerPosition, setRangePickerPosition] = useState({ top: 80, left: 16 });
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const previewDebounceRef = useRef(null);
  const rangeTriggerRef = useRef(null);
  const rangePopupRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    setActiveTab('backup');
    setShowConfirm(false);
    setShowRangePicker(false);
    setFromDate(null);
    setToDate(null);

    let cancelled = false;
    setLoadingInfo(true);
    fileExplorerService.getFileExplorerMaintenanceInfo()
      .then((data) => {
        if (cancelled) return;
        setLastBackup(data?.lastBackup || null);
        const range = data?.dateRange || null;
        setDateRange(range);

        // Default al abrir: "hace más de 1 año" (mismo criterio que la
        // maqueta) en vez de todo el histórico, para no sugerir por defecto
        // el borrado más amplio posible en una acción irreversible.
        const earliest = range ? new Date(range.earliest).getTime() : null;
        const latest = range ? new Date(range.latest).getTime() : null;
        if (earliest !== null && latest !== null) {
          setFromDate(new Date(earliest));
          setToDate(new Date(Math.max(earliest, latest - 365 * DAY_MS)));
        }
      })
      .catch(() => {
        if (!cancelled) setDateRange(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingInfo(false);
      });

    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (!showRangePicker) return undefined;
    const handleClickOutside = (event) => {
      if (rangePopupRef.current && !rangePopupRef.current.contains(event.target)) {
        setShowRangePicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showRangePicker]);

  const earliestMs = dateRange ? new Date(dateRange.earliest).getTime() : null;
  const latestMs = dateRange ? new Date(dateRange.latest).getTime() : null;
  // No exigimos latestMs > earliestMs: si todos los registros caen en un
  // mismo día (ej. solo hay archivos de "ayer") earliest y latest quedan
  // iguales, pero el filtro de período debe seguir disponible igual.
  const hasRange = earliestMs !== null && latestMs !== null;

  useEffect(() => {
    if (!isOpen || activeTab !== 'period' || !hasRange || !fromDate || !toDate) return undefined;

    setLoadingPreview(true);
    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);

    previewDebounceRef.current = setTimeout(() => {
      const from = fromDate.toISOString();
      const to = toDate.toISOString();

      fileExplorerService.getFileExplorerPeriodPreview({ from, to })
        .then((data) => setPreview(data))
        .catch(() => setPreview(null))
        .finally(() => setLoadingPreview(false));
    }, PREVIEW_DEBOUNCE_MS);

    return () => clearTimeout(previewDebounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, activeTab, fromDate, toDate, hasRange]);

  if (!isOpen) return null;

  const openRangePicker = () => {
    if (!hasRange) return;
    const trigger = rangeTriggerRef.current;
    if (trigger) {
      const rect = trigger.getBoundingClientRect();
      const margin = 8;
      const popupWidth = 340;
      let left = rect.left;
      const top = rect.bottom + margin;
      if (left + popupWidth > window.innerWidth - margin) left = window.innerWidth - popupWidth - margin;
      if (left < margin) left = margin;
      setRangePickerPosition({ top, left });
    }
    setShowRangePicker(true);
  };

  const handleRangeChange = (ranges) => {
    const nextStart = ranges.selection?.startDate || null;
    let nextEnd = ranges.selection?.endDate || null;
    if (nextStart && !nextEnd) nextEnd = nextStart;
    if (nextStart && nextEnd && nextEnd < nextStart) nextEnd = nextStart;

    setFromDate(nextStart);
    setToDate(nextEnd);
  };

  const handleGenerateBackup = async () => {
    setGeneratingBackup(true);
    try {
      const response = await fileExplorerService.generateFileExplorerBackup();
      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition') || '';
      const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
      const filename = filenameMatch ? filenameMatch[1] : 'backup-directorio.zip';

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      onNotify?.('Backup generado correctamente.', 'success');
      setLastBackup({ generatedAt: new Date().toISOString(), sizeBytes: blob.size, fileCount: lastBackup?.fileCount });
    } catch (err) {
      onNotify?.(mapMaintenanceErrorToMessage(err, 'generar el backup'), 'error');
    } finally {
      setGeneratingBackup(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!hasRange || !fromDate || !toDate) return;
    setDeleting(true);
    try {
      const from = fromDate.toISOString();
      const to = toDate.toISOString();
      const result = await fileExplorerService.deleteFileExplorerByPeriod({ from, to });
      const deletedFolders = result?.deletedFolders ?? 0;
      const deletedFiles = result?.deletedFiles ?? 0;

      onNotify?.(`Se eliminaron ${deletedFolders} carpeta(s) y ${deletedFiles} archivo(s) del período seleccionado.`, 'success');
      setShowConfirm(false);
      onDeleted?.();
    } catch (err) {
      onNotify?.(mapMaintenanceErrorToMessage(err, 'eliminar por período'), 'error');
      setShowConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const rangeDisplayValue = fromDate && toDate
    ? `${formatDisplayDate(fromDate)} - ${formatDisplayDate(toDate)}`
    : 'Seleccionar fechas';

  const summaryText = preview
    ? `${preview.folders ?? 0} carpetas · ${preview.files ?? 0} archivos · ${formatSize(preview.sizeBytes)}`
    : loadingPreview ? 'Calculando...' : '—';

  return (
    <>
      <div className="fixed inset-0 bg-[var(--app-overlay)] flex items-center justify-center z-9998 p-4" role="dialog" aria-modal="true">
        <div className="bg-surface rounded-lg shadow-xl border border-line w-full max-w-2xl max-h-[90vh] overflow-y-auto">

          <div className="sticky top-0 bg-surface z-10 flex items-start justify-between gap-3 p-6 border-b border-line">
            <div className="flex items-start gap-3">
              <div className="flex items-center justify-center w-10 h-10 rounded-md bg-amber/15 text-accent-text shrink-0">
                <Database size={20} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-content">Mantenimiento del directorio</h2>
                <p className="text-sm text-content-muted">
                  Backup completo o borrado masivo por rango de fechas — aplica a todo el árbol, no a la selección actual.
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-content-muted hover:text-content shrink-0" type="button" title="Cerrar">
              <X size={20} />
            </button>
          </div>

          <div className="p-6 flex flex-col gap-5">
            <div className="inline-flex self-start gap-2 rounded-2xl border border-line bg-surface/80 p-2">
              <button
                type="button"
                onClick={() => setActiveTab('backup')}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                  activeTab === 'backup' ? 'bg-amber text-on-amber shadow-lg shadow-amber/20' : 'text-content-muted hover:bg-surface-2 hover:text-content'
                }`}
              >
                <CloudArrowDown size={16} />
                Backup
              </button>
              <button
                type="button"
                onClick={() => setActiveTab('period')}
                className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${
                  activeTab === 'period' ? 'bg-amber text-on-amber shadow-lg shadow-amber/20' : 'text-content-muted hover:bg-surface-2 hover:text-content'
                }`}
              >
                <Warning size={16} className="text-error" />
                Borrar por período
              </button>
            </div>

            {activeTab === 'backup' && (
              <div className="flex flex-col gap-3">
                <p className="text-sm text-content-muted leading-relaxed">
                  Genera un archivo .zip con una copia completa de todos los archivos y carpetas de la raíz.
                  Puedes hacerlo en cualquier momento, no reemplaza los archivos originales.
                </p>

                <div className="flex flex-wrap items-center justify-between gap-3 bg-bg border border-line rounded-lg px-4 py-3">
                  <div className="text-sm text-content">
                    {loadingInfo ? (
                      'Cargando información del último backup...'
                    ) : lastBackup ? (
                      <>
                        Último backup: <strong className="text-content">{formatDateTime(lastBackup.generatedAt)}</strong>
                        {' '}
                        <span className="text-content-muted">
                          — {formatSize(lastBackup.sizeBytes)}
                          {lastBackup.fileCount ? `, ${lastBackup.fileCount} archivos` : ''}
                        </span>
                      </>
                    ) : (
                      'Todavía no se generó ningún backup.'
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleGenerateBackup}
                    disabled={generatingBackup}
                    className="inline-flex items-center gap-2 rounded-md bg-amber hover:bg-amber-hover text-on-amber font-semibold px-4 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <CloudArrowDown size={16} />
                    {generatingBackup ? 'Generando backup...' : 'Generar backup ahora'}
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'period' && (
              <div className="flex flex-col gap-4">
                <p className="text-sm text-content-muted leading-relaxed">
                  Elimina todos los archivos y carpetas cuya fecha de modificación caiga dentro del rango elegido.{' '}
                  <strong className="text-error">Esta acción no se puede deshacer.</strong>
                </p>

                {!loadingInfo && !hasRange ? (
                  <p className="text-sm text-content-muted italic">No hay suficiente historial para elegir un período.</p>
                ) : (
                  <div className="flex items-end gap-3">
                    <div className="relative flex-1">
                      <label className="block text-xs font-medium text-content mb-1">
                        Rango de fechas
                      </label>
                      <button
                        type="button"
                        ref={rangeTriggerRef}
                        onClick={openRangePicker}
                        disabled={loadingInfo}
                        className="w-full px-3 py-2.5 text-sm border border-line rounded-lg bg-surface-2 text-content text-left flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-amber disabled:cursor-not-allowed disabled:opacity-50"
                        aria-expanded={showRangePicker}
                      >
                        <span>{rangeDisplayValue}</span>
                        <CalendarBlank size={18} className="text-content-muted ml-2" />
                      </button>

                      {showRangePicker && (
                        <div
                          ref={rangePopupRef}
                          className="fixed z-[9999] w-max min-w-[320px] max-w-[95vw] rounded-lg border border-line bg-surface-2 p-2 shadow-2xl overflow-y-auto"
                          style={{
                            top: `${rangePickerPosition.top}px`,
                            left: `${rangePickerPosition.left}px`,
                            maxHeight: `calc(100vh - ${rangePickerPosition.top}px - 8px)`,
                            boxShadow: '0 8px 32px 0 rgba(0,0,0,0.35)'
                          }}
                        >
                          <DateRange
                            ranges={[{ startDate: fromDate || new Date(earliestMs), endDate: toDate || new Date(latestMs), key: 'selection' }]}
                            onChange={handleRangeChange}
                            minDate={hasRange ? new Date(earliestMs) : undefined}
                            maxDate={hasRange ? new Date(latestMs) : undefined}
                            className="generic-date-range__picker"
                            months={1}
                            showDateDisplay={false}
                            moveRangeOnFirstSelection={false}
                            rangeColors={['var(--amber)']}
                            locale={es}
                          />
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      onClick={() => setShowConfirm(true)}
                      className="inline-flex items-center gap-2 rounded-md bg-error hover:bg-error/85 text-white font-semibold px-4 py-2.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-45 shrink-0"
                    >
                      <Trash size={16} />
                      Eliminar
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="px-6 py-4 border-t border-line flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 bg-surface-2 hover:bg-line text-content rounded-lg transition-colors font-medium"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showConfirm}
        variant="danger"
        title="Confirmar eliminación masiva"
        message={`Vas a eliminar de forma permanente ${summaryText} correspondientes al período ${formatDayMonthYear(fromDate?.toISOString())} – ${formatDayMonthYear(toDate?.toISOString())}. Esta acción no se puede deshacer.`}
        confirmText={deleting ? 'Eliminando...' : 'Eliminar definitivamente'}
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowConfirm(false)}
      />
    </>
  );
};

export default FileExplorerMaintenanceModal;
